import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { breadcrumbSchema, jsonLd, absUrl } from "@/lib/seo";
import { internalPath } from "@/lib/paths";

const timeline = [
  { year: "2018", body: "Tailor's Barber grundas i Täby Centrum – vår första och än idag flaggskepp-salong." },
  { year: "2020", body: "Andra salongen öppnar i centrala Stockholm, inne i Åhléns City – ett stenkast från T-Centralen." },
  { year: "2023", body: "Expansion till Åkersberga med en salong i ICA Maxi." },
  { year: "2025", body: "Fjärde salongen öppnar i Arninge ICA Maxi i norra Täby." },
];

const values = [
  { title: "Hantverk framför allt", body: "Varje klippning är ett hantverk. Vi tar oss tid, konsulterar och anpassar resultatet – aldrig en mall." },
  { title: "Tillgänglighet", body: "Fyra salonger, öppna alla dagar, med kvällsöppet och drop-in. Vi ska finnas där du är." },
  { title: "Gemenskap", body: "Vår salong är inte bara en plats för klippning. Det är en plats där herrar kan ta en paus, prata med barberaren och gå därifrån med mer energi än de kom med." },
];

const salons: { name: string; since: string; to: "/barberare-stockholm-taby" | "/barberare-stockholm-ahlens-city" | "/barberare-stockholm-akersberga" | "/barberare-stockholm-arninge" }[] = [
  { name: "Täby Centrum", since: "2018", to: "/barberare-stockholm-taby" },
  { name: "Åhléns City, Stockholm", since: "2020", to: "/barberare-stockholm-ahlens-city" },
  { name: "Åkersberga ICA Maxi", since: "2023", to: "/barberare-stockholm-akersberga" },
  { name: "Arninge ICA Maxi", since: "2025", to: "/barberare-stockholm-arninge" },
];

export const Route = createFileRoute("/om-oss")({
  head: () => ({
    meta: [
      { title: "Om Tailor's Barber: barbershopkedja i Stockholm sedan 2018" },
      { name: "description", content: "Om Tailor's Barber, grundad 2018 i Täby Centrum. Idag fyra salonger i Stockholm med herrklippning, fade och skägg. Boka online." },
      { property: "og:title", content: "Om Tailor's Barber: barbershopkedja i Stockholm sedan 2018" },
      { property: "og:description", content: "Tailor's Barber grundades 2018. Idag fyra salonger i Stockholm. Boka online." },
      { property: "og:url", content: absUrl("/om-oss") },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: absUrl("/om-oss") }],
    scripts: [
      jsonLd(breadcrumbSchema([{ name: "Hem", url: "/" }, { name: "Om oss", url: "/om-oss" }])),
    ],
  }),
  component: OmOssPage,
});

function OmOssPage() {
  return (
    <SiteShell>
      <section className="container-page pt-10 md:pt-16 pb-4">
        <nav className="text-xs text-muted-foreground mb-6">
          <Link to="/" className="hover:underline">Hem</Link>
          <span className="mx-1.5">/</span>
          <span className="text-foreground">Om oss</span>
        </nav>
        <div className="eyebrow">Om Tailor&apos;s Barber</div>
        <h1 className="mt-3 text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
          Om Tailor&apos;s Barber
        </h1>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Vår historia</h2>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          Tailor&apos;s Barber grundades 2018 med en tydlig vision: att skapa en barbershop där
          kvalitet, upplevelse och tillgänglighet står i centrum. Vår resa började i Täby Centrum –
          vår allra första och än idag flaggskepp-salong – med ambitionen att erbjuda professionell
          herrklippning och skäggvård till ett pris som kombinerar alla tre.
        </p>
        <ol className="mt-8 space-y-5 max-w-3xl">
          {timeline.map((t) => (
            <li key={t.year} className="card-elev p-6 flex gap-5">
              <span className="text-2xl font-medium text-primary shrink-0">{t.year}</span>
              <span className="text-foreground/85 leading-relaxed">{t.body}</span>
            </li>
          ))}
        </ol>
        <p className="mt-8 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          Idag är Tailor&apos;s Barber en av Stockholms mest välbesökta barbershopkedjor med ett team
          av dedikerade barberare på fyra salonger.
        </p>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Vår vision</h2>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          Att vara den mest tillförlitliga och uppskattade barbersalongen i Stockholm – och på sikt i
          hela Sverige. Vi strävar efter att vara det självklara valet för den man som vill ha en
          frisyr och skäggstil han är stolt över.
        </p>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Vad vi tror på</h2>
        <ul className="mt-6 grid gap-5 md:grid-cols-3">
          {values.map((v) => (
            <li key={v.title} className="card-elev p-6">
              <h3 className="text-lg font-medium">{v.title}</h3>
              <p className="mt-2 text-foreground/80 leading-relaxed">{v.body}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Våra salonger</h2>
        <ul className="mt-6 grid gap-4 md:grid-cols-2 max-w-4xl">
          {salons.map((s) => (
            <li key={s.name} className="card-elev p-5 flex items-center justify-between gap-4">
              <div>
                <div className="font-medium">{s.name}</div>
                <div className="text-sm text-foreground/70">Sedan {s.since}</div>
              </div>
              <Link to={internalPath(s.to)} className="btn-outline text-sm">Mer info</Link>
            </li>
          ))}
        </ul>
      </section>

      <section className="container-page py-10">
        <p className="text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          Vill du veta hur vi står oss mot andra i stan? Vi går igenom salonger, betyg och priser i{" "}
          <Link to="/basta-barberare-i-stockholm-2026/" className="underline font-medium">
            Bästa barberare i Stockholm 2026
          </Link>
          .
        </p>
      </section>

      <section className="container-page pb-16">
        <h2 className="text-2xl md:text-3xl">Följ oss</h2>
        <ul className="mt-4 flex flex-wrap gap-3">
          <li>
            <a href="https://www.instagram.com/tailorsbarber" target="_blank" rel="noopener noreferrer" className="btn-outline">
              Instagram @tailorsbarber
            </a>
          </li>
          <li>
            <a href="https://www.facebook.com/tailorsbarber" target="_blank" rel="noopener noreferrer" className="btn-outline">
              Facebook /tailorsbarber
            </a>
          </li>
          <li>
            <a href="https://www.tiktok.com/@tailorsbarber.se" target="_blank" rel="noopener noreferrer" className="btn-outline">
              TikTok @tailorsbarber.se
            </a>
          </li>
        </ul>
      </section>
    </SiteShell>
  );
}
